import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, Button, Space, Typography, Spin, Alert, Breadcrumb } from 'antd';
import { ArrowLeftOutlined, HomeOutlined, UserOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useSelector, useDispatch } from 'react-redux';
import DynamicFormSP from '../../components/crud/DynamicFormSP.component';
import SingleEntitySelectMiniature from '../../components/crud/SingleEntitySelectMiniature.component';
import { 
  loadSubjectDto,
  createSubject,
  updateSubject,
  deleteSubject,
  getSubjectDetails,
  listSubjects,
  listSubjectsByIds
} from '../../actions/subjectActions';
import { listCohorts, listCohortsByIds } from '../../actions/cohortActions';
import { listPatients, listPatientsByIds } from '../../actions/patientActions';

const { Title, Text } = Typography;

const SubjectDetailScreen = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [subjectData, setSubjectData] = useState(null);

  // Redux state
  const subjectDetails = useSelector(state => state.subjectDetails);
  const { 
    loading: loadingDetails, 
    subject: subjectFromDetails, 
    error: errorDetails 
  } = subjectDetails;

  // Load subject details when component mounts
  useEffect(() => {
    if (id && id !== 'new') {
      dispatch(getSubjectDetails(id));
    }
  }, [dispatch, id]);

  // Update local subject data when Redux data changes
  useEffect(() => {
    if (subjectFromDetails) {
      setSubjectData(subjectFromDetails);
    }
  }, [subjectFromDetails]);

  // Handle errors
  useEffect(() => {
    if (errorDetails) {
      setError(Array.isArray(errorDetails) ? errorDetails.join(', ') : errorDetails);
    }
  }, [errorDetails]);

  const handleGoBack = () => {
    navigate('/patients/cohorts');
  };

  const handleSave = (updatedSubject) => {
    if (id === 'new') {
      dispatch(createSubject(updatedSubject));
    } else {
      dispatch(updateSubject(id, updatedSubject));
    }
  };

  const handleDelete = () => {
    if (id && id !== 'new') {
      dispatch(deleteSubject(id));
      navigate('/patients/cohorts');
    }
  };

  const breadcrumbItems = [
    {
      href: '/',
      title: <HomeOutlined />,
    },
    {
      href: '/patients/cohorts',
      title: (
        <span>
          <UserOutlined />
          <span>{t('Subjects')}</span>
        </span>
      ),
    },
    {
      title: id === 'new' ? t('New Subject') : (subjectData?.subjectIdentifier || t('Subject Details')),
    },
  ];

  if (loadingDetails || loading) {
    return (
      <div style={{ padding: 24, textAlign: 'center' }}>
        <Spin size="large" />
        <div style={{ marginTop: 16 }}>
          <Text>{t('Loading subject details...')}</Text>
        </div>
      </div>
    );
  }

  if (error && id !== 'new') {
    return (
      <div style={{ padding: 24 }}>
        <Alert
          message={t('Error loading subject')}
          description={error}
          type="error"
          showIcon
          action={
            <Button size="small" onClick={handleGoBack}>
              {t('Go Back')}
            </Button>
          }
        />
      </div>
    );
  }

  return (
    <div style={{ padding: 24 }}>
      {/* Header */}
      <div style={{ marginBottom: 24 }}>
        <Breadcrumb items={breadcrumbItems} style={{ marginBottom: 16 }} />
        
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <Title level={2} style={{ marginBottom: 8 }}>
              {id === 'new' ? t('Create New Subject') : t('Edit Subject')}
            </Title>
            {subjectData && (
              <Text type="secondary">
                {subjectData.subjectIdentifier} - {subjectData.status}
              </Text>
            )}
          </div>
          
          <Button 
            icon={<ArrowLeftOutlined />} 
            onClick={handleGoBack}
            type="default"
          >
            {t('Back to Subjects')}
          </Button>
        </div>
      </div>

      {/* Subject Information Card */}
      {subjectData && id !== 'new' && (
        <Card 
          title={t('Quick Overview')} 
          size="small" 
          style={{ marginBottom: 24 }}
        >
          <Space wrap size="large">
            <div>
              <Text strong>{t('Subject ID')}:</Text>
              <br />
              <Text>{subjectData.subjectIdentifier}</Text>
            </div>
            <div>
              <Text strong>{t('Status')}:</Text>
              <br />
              <Text>{subjectData.status}</Text>
            </div>
            <div>
              <Text strong>{t('Enrollment Status')}:</Text>
              <br />
              <Text>{subjectData.enrollmentStatus}</Text>
            </div>
            <div>
              <Text strong>{t('Approval Status')}:</Text>
              <br />
              <Text>{subjectData.approvalStatus}</Text>
            </div>
            <div>
              <Text strong>{t('Cohort')}:</Text>
              <br />
              <SingleEntitySelectMiniature
                singularEntity="cohort"
                pluralEntity="cohorts"
                listEntities={listCohorts}
                listEntitiesByIds={listCohortsByIds}
                value={subjectData.cohortId}
                primarySearchProp="name"
                secondarySearchProp="description"
                enableCreate={false}
                enableEditing={false}
                placeholder={t('No cohort assigned')}
              />
            </div>
            <div>
              <Text strong>{t('Patient')}:</Text>
              <br />
              <SingleEntitySelectMiniature
                singularEntity="patient"
                pluralEntity="patients"
                listEntities={listPatients}
                listEntitiesByIds={listPatientsByIds}
                value={subjectData.patientId}
                primarySearchProp="firstName"
                secondarySearchProp="lastName"
                enableCreate={false}
                enableEditing={false}
                placeholder={t('No patient assigned')}
              />
            </div>
          </Space>
        </Card>
      )}

      {/* Dynamic Form */}
      <Card 
        title={id === 'new' ? t('Subject Information') : t('Edit Subject Details')}
        style={{ marginBottom: 24 }}
      >
        <DynamicFormSP
          singularEntity="subject"
          pluralEntity="subjects"
          constantPrefix="SUBJECT"
          loadDto={loadSubjectDto}
          createEntity={handleSave}
          updateEntity={handleSave}
          deleteEntity={handleDelete}
          getEntityDetails={getSubjectDetails}
          listEntities={listSubjects}
          listEntitiesByIds={listSubjectsByIds}
          
          defaultEditMode={id === 'new' ? 'Create' : 'Update'}
          entityId={id === 'new' ? null : id}
          initialData={subjectData}
          
          // UI Configuration
          enableCreate={id === 'new'}
          enableUpdate={id !== 'new'}
          enableDelete={id !== 'new'}
          enableSearchBar={false}
          enableActionButtons={true}
          enablePagination={false}
          
          // Form customization
          fieldsToHide={id === 'new' ? [] : ['Id']}
          fieldsToShow={[
            'SubjectIdentifier',
            'PatientId',
            'CohortId', 
            'Postcode',
            'StudyRole',
            'Status',
            'EnrollmentStatus',
            'ApprovalStatus',
            'ApprovalReason',
            'EnrollmentDate',
            'ConsentStatus',
            'ConsentDate',
            'CompletionDate',
            'WithdrawalDate',
            'WithdrawalReason',
            'Notes',
            'IsNHSNumberValid',
            'IsInGeographicalScope',
            'IsSelectedForSubmission'
          ]}
          
          // Navigation
          onSaveSuccess={() => {
            setTimeout(() => {
              navigate('/patients/cohorts');
            }, 1500);
          }}
          onDeleteSuccess={() => {
            navigate('/patients/cohorts');
          }}
          
          // Layout
          isParent={true}
          showFormOnly={true}
        />
      </Card>
    </div>
  );
};

export default SubjectDetailScreen;
